import { useState, useEffect, useMemo } from 'react';
import {
    Typography,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, CircularProgress, Alert, Card, CardContent, Box, Chip,
    TextField, InputAdornment, IconButton, MenuItem, Select, FormControl, Stack,
    TableSortLabel, TablePagination, Tooltip, Divider
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import ViewCompactIcon from '@mui/icons-material/ViewCompact';
import ViewComfyIcon from '@mui/icons-material/ViewComfy';

const DocumentsClients = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError]   = useState(null);

    // UI state
    const [query, setQuery] = useState('');
    const [filter, setFilter] = useState('all'); // all | pending | nodocs
    const [order, setOrder] = useState('desc');  // asc | desc
    const [orderBy, setOrderBy] = useState('codeClient'); 
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [dense, setDense] = useState(false);

    const fetchData = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetch('/api/comptable/clients-documents', {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
            });
            if (!response.ok) throw new Error('Erreur lors de la récupération des données');
            const result = await response.json();
            setData(result || []);
        } catch (err) {
            setError(err.message || 'Erreur inconnue');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchData(); }, []);

    const isPending = (doc = {}) => {
        const s = (
            doc.status || doc.statut || doc.state || doc.processingStatus || doc.etat || ''
        ).toString().toLowerCase();

        if (typeof doc.pending === 'boolean') return doc.pending === true;
        if (typeof doc.isPending === 'boolean') return doc.isPending === true;

        return ['pending', 'en_attente', 'attente', 'in_progress', 'processing'].includes(s);
    };

    const bestDateFromDoc = (doc = {}) => {
        const cand = [
            doc.updatedAt ? new Date(doc.updatedAt) : null,
            doc.createdAt ? new Date(doc.createdAt) : null,
        ].filter(Boolean);
        if (cand.length === 0) return null;
        return new Date(Math.max(...cand.map(d => d.getTime())));
    };

    const formatClientLabel = (client = {}) => {
        const rs = client.raisonSociale && client.raisonSociale.trim();
        const names = [client.nom, client.prenom].filter(Boolean).join(' ').trim();
        return rs || names || '—';
    };

    const relativeTimeFr = (date) => {
        if (!date) return '';
        const now = new Date().getTime();
        const diffSec = Math.floor((now - date.getTime()) / 1000);
        if (diffSec < 10) return "à l’instant";
        if (diffSec < 60) return `il y a ${diffSec}s`;
        const diffMin = Math.floor(diffSec / 60);
        if (diffMin < 60) return `il y a ${diffMin} min`;
        const diffH = Math.floor(diffMin / 60);
        if (diffH < 24) return `il y a ${diffH} h`;
        const diffD = Math.floor(diffH / 24);
        return `il y a ${diffD} j`;
    };

    const rows = useMemo(() => {
        if (!Array.isArray(data)) return [];
        return data.map(item => {
            const client = item?.client || {};
            const docs = Array.isArray(item?.documents) ? item.documents : [];
            const totalDocs = docs.length;
            const pendingDocs = docs.reduce((acc, d) => acc + (isPending(d) ? 1 : 0), 0);

            let last = docs.map(bestDateFromDoc).filter(Boolean);
            if (last.length === 0 && client?.updatedAt) {
                last = [new Date(client.updatedAt)];
            }
            const lastUpdate = last.length ? new Date(Math.max(...last.map(d => d.getTime()))) : null;

            return {
                id: client._id || Math.random().toString(36).slice(2),
                clientLabel: formatClientLabel(client),
                codeClient: client.codeClient ?? 0,
                totalDocs,
                pendingDocs,
                lastUpdate,
            };
        });
    }, [data]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        return rows.filter(r => {
            const passQuery = q === '' || r.clientLabel.toLowerCase().includes(q);
            const passFilter =
                filter === 'all' ||
                (filter === 'pending' && r.pendingDocs > 0) ||
                (filter === 'nodocs' && r.totalDocs === 0);
            return passQuery && passFilter;
        });
    }, [rows, query, filter]);

    const comparator = (a, b) => {
        const get = (r, key) => {
            if (key === 'codeClient')  return r.codeClient ?? 0;
            if (key === 'clientLabel') return r.clientLabel || '';
            if (key === 'totalDocs')  return r.totalDocs ?? 0;
            if (key === 'pendingDocs')return r.pendingDocs ?? 0;
            if (key === 'lastUpdate') return r.lastUpdate ? r.lastUpdate.getTime() : 0;
            return 0;
        };
        const va = get(a, orderBy);
        const vb = get(b, orderBy);
        if (va < vb) return order === 'asc' ? -1 : 1;
        if (va > vb) return order === 'asc' ? 1 : -1;
        return 0;
    };
    const sorted = useMemo(() => [...filtered].sort(comparator), [filtered, order, orderBy]);

    const paged = useMemo(
        () => sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
        [sorted, page, rowsPerPage]
    );

    const handleRequestSort = (key) => {
        if (orderBy === key) {
            setOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));
        } else {
            setOrderBy(key);
            setOrder(key === 'clientLabel' ? 'asc' : 'desc');
        }
    };

    const handleChangePage = (_, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (e) => {
        setRowsPerPage(parseInt(e.target.value, 10));
        setPage(0);
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" py={6}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box py={4}>
                <Alert severity="error">{error}</Alert>
            </Box>
        );
    }

    return (
        <Box>
            <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={2}
                        alignItems={{ xs: 'stretch', sm: 'center' }}
                        justifyContent="space-between"
                    >
                        <Box>
                            <Typography variant="body2" color="text.secondary">
                                {rows.length} client(s) • {rows.reduce((a, r) => a + r.totalDocs, 0)} document(s)
                            </Typography>
                        </Box>

                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                            <TextField
                                value={query}
                                onChange={(e) => { setQuery(e.target.value); setPage(0); }}
                                placeholder="Rechercher un client…"
                                size="small"
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                    ),
                                }}
                            />

                            <FormControl size="small" sx={{ minWidth: 150 }}>
                                <Select value={filter} onChange={(e) => { setFilter(e.target.value); setPage(0); }}>
                                    <MenuItem value="all">Tous</MenuItem>
                                    <MenuItem value="pending">Avec en attente</MenuItem>
                                    <MenuItem value="nodocs">Sans documents</MenuItem>
                                </Select>
                            </FormControl>

                            <Tooltip title="Rafraîchir">
                                <IconButton onClick={fetchData}>
                                    <RefreshIcon />
                                </IconButton>
                            </Tooltip>

                            <Tooltip title={dense ? 'Affichage confortable' : 'Affichage compact'}>
                                <IconButton onClick={() => setDense(d => !d)}>
                                    {dense ? <ViewComfyIcon /> : <ViewCompactIcon />}
                                </IconButton>
                            </Tooltip>
                        </Stack>
                    </Stack>

                    <Divider sx={{ mt: 2 }} />
                </CardContent>
            </Card>

            {rows.length === 0 ? (
                <Alert severity="info">Aucun client avec documents trouvés.</Alert>
            ) : (
                <Card variant="outlined">
                    <CardContent sx={{ p: 0 }}>
                        <TableContainer component={Paper} elevation={0}>
                            <Table size={dense ? 'small' : 'medium'} stickyHeader>
                                <TableHead>
                                    <TableRow sx={{ '& th': { fontWeight: 600 } }}>
                                        <TableCell sortDirection={orderBy === 'clientLabel' ? order : false}>
                                            <TableSortLabel
                                                active={orderBy === 'clientLabel'}
                                                direction={orderBy === 'clientLabel' ? order : 'asc'}
                                                onClick={() => handleRequestSort('clientLabel')}
                                            >
                                                Client / Raison sociale
                                            </TableSortLabel>
                                        </TableCell>

                                        <TableCell align="right" sortDirection={orderBy === 'totalDocs' ? order : false}>
                                            <TableSortLabel
                                                active={orderBy === 'totalDocs'}
                                                direction={orderBy === 'totalDocs' ? order : 'desc'}
                                                onClick={() => handleRequestSort('totalDocs')}
                                            >
                                                Documents
                                            </TableSortLabel>
                                        </TableCell>

                                        <TableCell align="right" sortDirection={orderBy === 'pendingDocs' ? order : false}>
                                            <TableSortLabel
                                                active={orderBy === 'pendingDocs'}
                                                direction={orderBy === 'pendingDocs' ? order : 'desc'}
                                                onClick={() => handleRequestSort('pendingDocs')}
                                            >
                                                En attente
                                            </TableSortLabel>
                                        </TableCell>

                                        <TableCell sortDirection={orderBy === 'lastUpdate' ? order : false}>
                                            <TableSortLabel
                                                active={orderBy === 'lastUpdate'}
                                                direction={orderBy === 'lastUpdate' ? order : 'desc'}
                                                onClick={() => handleRequestSort('lastUpdate')}
                                            >
                                                Dernière mise à jour
                                            </TableSortLabel>
                                        </TableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {paged.map(row => {
                                        const lastStr = row.lastUpdate
                                            ? row.lastUpdate.toLocaleString('fr-FR', {
                                                year: 'numeric', month: '2-digit', day: '2-digit',
                                                hour: '2-digit', minute: '2-digit', second: '2-digit'
                                            })
                                            : '—';
                                        const rel = relativeTimeFr(row.lastUpdate);

                                        return (
                                            <TableRow
                                                key={row.id}
                                                hover
                                                sx={{
                                                    '&:nth-of-type(odd)': { bgcolor: 'action.hover' }
                                                }}
                                            >
                                                <TableCell>
                                                    <Typography fontWeight={600}>{row.clientLabel}</Typography>
                                                </TableCell>

                                                <TableCell align="right">
                                                    <Chip label={row.totalDocs} size="small" />
                                                </TableCell>

                                                <TableCell align="right">
                                                    <Chip
                                                        label={row.pendingDocs}
                                                        size="small"
                                                        color={row.pendingDocs > 0 ? 'warning' : 'success'}
                                                        variant={row.pendingDocs > 0 ? 'filled' : 'outlined'}
                                                    />
                                                </TableCell>

                                                <TableCell>
                                                    <Stack spacing={0}>
                                                        <Typography>{lastStr}</Typography>
                                                        <Typography variant="caption" color="text.secondary">{rel}</Typography>
                                                    </Stack>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>

                            <TablePagination
                                component="div"
                                count={sorted.length}
                                page={page}
                                onPageChange={handleChangePage}
                                rowsPerPage={rowsPerPage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                rowsPerPageOptions={[5, 10, 25, 50]}
                            />
                        </TableContainer>
                    </CardContent>
                </Card>
            )}
        </Box>
    );
};

export default DocumentsClients;
